import React, { useState, useEffect, useCallback } from 'react';
import { 
  CreditCard, 
  Receipt, 
  RefreshCw, 
  Download, 
  FileText, 
  ChevronDown, 
  ChevronUp,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  AlertCircle,
  CheckCircle2,
  XCircle,
  User,
  Filter,
  ExternalLink,
  RotateCcw,
  Loader2
} from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { useTimezone } from '../../hooks/useTimezone';
import api from '../../services/api';

const EVENT_TYPES = {
  // Stripe events
  charge: { label: 'Charge', icon: CreditCard, color: 'text-green-500' },
  refund: { label: 'Refund', icon: ArrowDownRight, color: 'text-orange-500' },
  invoice: { label: 'Invoice', icon: Receipt, color: 'text-blue-500' },
  subscription_created: { label: 'Subscription Started', icon: CheckCircle2, color: 'text-green-500' },
  subscription_updated: { label: 'Subscription Updated', icon: RefreshCw, color: 'text-yellow-500' },
  subscription_cancelled: { label: 'Subscription Cancelled', icon: XCircle, color: 'text-red-500' },
  payment_failed: { label: 'Payment Failed', icon: AlertCircle, color: 'text-red-500' },
  
  // Internal events
  plan_change: { label: 'Plan Change', icon: ArrowUpRight, color: 'text-purple-500' },
  seat_added: { label: 'Seats Added', icon: User, color: 'text-green-500' },
  seat_removed: { label: 'Seats Removed', icon: User, color: 'text-orange-500' },
  billing_info_updated: { label: 'Billing Updated', icon: FileText, color: 'text-blue-500' },
  upgrade_requested: { label: 'Upgrade Requested', icon: ArrowUpRight, color: 'text-purple-500' },
  downgrade_initiated: { label: 'Downgrade Initiated', icon: ArrowDownRight, color: 'text-orange-500' },
  admin_plan_override: { label: 'Admin Override', icon: AlertCircle, color: 'text-yellow-500' },
  grace_period_granted: { label: 'Grace Period', icon: Clock, color: 'text-blue-500' },
  payment_method_added: { label: 'Payment Method Added', icon: CreditCard, color: 'text-green-500' },
  payment_method_removed: { label: 'Payment Method Removed', icon: CreditCard, color: 'text-red-500' },
};

const STATUS_STYLES = {
  succeeded: { bg: 'bg-green-500/10', text: 'text-green-500', label: 'Succeeded' },
  pending: { bg: 'bg-yellow-500/10', text: 'text-yellow-500', label: 'Pending' },
  failed: { bg: 'bg-red-500/10', text: 'text-red-500', label: 'Failed' },
  refunded: { bg: 'bg-orange-500/10', text: 'text-orange-500', label: 'Refunded' },
  cancelled: { bg: 'bg-gray-500/10', text: 'text-gray-500', label: 'Cancelled' },
  paid: { bg: 'bg-green-500/10', text: 'text-green-500', label: 'Paid' },
  open: { bg: 'bg-blue-500/10', text: 'text-blue-500', label: 'Open' },
  draft: { bg: 'bg-gray-500/10', text: 'text-gray-500', label: 'Draft' },
  void: { bg: 'bg-gray-500/10', text: 'text-gray-500', label: 'Void' },
  active: { bg: 'bg-green-500/10', text: 'text-green-500', label: 'Active' },
};

const formatCurrency = (cents, currency = 'USD') => {
  if (cents === null || cents === undefined) return '-';
  const amount = cents / 100;
  const isNegative = amount < 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(Math.abs(amount)) + (isNegative ? ' (credit)' : '');
};

// Note: formatDate is now provided via useTimezone hook inside the component

const BillingAuditLog = ({ organizationId = null, isAdmin = false, stripeCustomerId = null }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const { formatDateTime } = useTimezone();
  
  // Create a local formatDate function that uses the user's timezone
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return formatDateTime(dateStr);
  };
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [auditInfo, setAuditInfo] = useState({
    organization_name: '',
    current_plan: '',
    subscription_status: '',
    total_count: 0,
    has_more: false
  });
  
  // Refund modal state
  const [refundModal, setRefundModal] = useState(null); // the event to refund
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refunding, setRefunding] = useState(false);
  
  const fetchAuditLog = useCallback(async (showRefreshToast = false) => {
    try {
      if (showRefreshToast) setRefreshing(true);
      else setLoading(true);
      
      const endpoint = isAdmin && organizationId 
        ? `/api/admin/billing/audit-log/${organizationId}`
        : `/api/billing/audit-log`;
      
      const params = new URLSearchParams();
      params.append('limit', '100');
      if (filterType !== 'all') params.append('event_type', filterType);
      
      const response = await api.get(`${endpoint}?${params}`);
      
      setEvents(response.data.events || []);
      setAuditInfo({
        organization_name: response.data.organization_name,
        current_plan: response.data.current_plan,
        subscription_status: response.data.subscription_status,
        total_count: response.data.total_count,
        has_more: response.data.has_more
      });
      setError(null);
      
      if (showRefreshToast) {
        toast.success('Billing history refreshed');
      }
    } catch (err) {
      console.error('Error fetching audit log:', err);
      setError(err.message);
      toast.error('Failed to load billing history');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [isAdmin, organizationId, filterType]);
  
  useEffect(() => {
    fetchAuditLog();
  }, [fetchAuditLog]);
  
  const getEventConfig = (eventType) => {
    return EVENT_TYPES[eventType] || { 
      label: eventType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), 
      icon: FileText, 
      color: 'text-gray-500' 
    };
  };
  
  const getStatusStyle = (status) => {
    return STATUS_STYLES[status] || STATUS_STYLES.pending;
  };
  
  const toggleEventExpand = (eventId) => {
    setExpandedEvent(expandedEvent === eventId ? null : eventId);
  };

  const openRefundModal = (event) => {
    // Pre-fill with full amount (convert cents to dollars)
    const amountInDollars = event.amount_cents ? (event.amount_cents / 100).toFixed(2) : '';
    setRefundAmount(amountInDollars);
    setRefundReason('');
    setRefundModal(event);
  };

  const handleRefund = async () => {
    if (!refundModal) return;
    
    const amountCents = Math.round(parseFloat(refundAmount) * 100);
    if (isNaN(amountCents) || amountCents <= 0) {
      toast.error('Please enter a valid refund amount');
      return;
    }
    
    if (amountCents > refundModal.amount_cents) {
      toast.error('Refund amount cannot exceed the original charge');
      return;
    }

    try {
      setRefunding(true);
      
      await api.post('/api/admin/billing/refund', {
        charge_id: refundModal.stripe_id || refundModal.event_id,
        amount_cents: amountCents,
        reason: refundReason || 'Admin initiated refund',
        organization_id: organizationId
      });

      toast.success('Refund processed successfully');
      setRefundModal(null);
      fetchAuditLog(true); // Refresh to show the refund event
    } catch (err) {
      console.error('Refund error:', err);
      toast.error(err.response?.data?.detail || 'Failed to process refund');
    } finally {
      setRefunding(false);
    }
  };

  // Check if an event can be refunded (charges that succeeded and aren't already refunded)
  const canRefund = (event) => {
    if (!isAdmin) return false;
    const refundableTypes = ['charge', 'invoice'];
    const refundableStatuses = ['succeeded', 'paid'];
    return refundableTypes.includes(event.event_type) && 
           refundableStatuses.includes(event.status) &&
           event.amount_cents > 0;
  };
  
  if (loading) {
    return (
      <Card className={cn("p-6", colors.cardBg)}>
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin mr-2" />
          <span className={colors.textMuted}>Loading billing history...</span>
        </div>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card className={cn("p-6", colors.cardBg)}>
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <p className="text-red-500 mb-4">{error}</p>
          <Button onClick={() => fetchAuditLog()} variant="outline">
            Try Again
          </Button>
        </div>
      </Card>
    );
  }
  
  // Check for comped/admin-provisioned accounts (non-admin view only)
  // These accounts have complimentary access and don't have billing history
  if (!isAdmin && auditInfo.subscription_status === 'active_admin') {
    return (
      <Card className={cn("p-6", colors.cardBg)}>
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h3 className={cn("text-lg font-semibold mb-2", colors.textPrimary)}>
            Complimentary Access
          </h3>
          <p className={cn("text-sm max-w-md mx-auto", colors.textMuted)}>
            This account has been granted complimentary access by an administrator. 
            No billing or payment is required.
          </p>
          <div className="mt-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg inline-block">
            <p className={cn("text-sm", colors.textSecondary)}>
              <span className="font-medium">Current Plan:</span>{' '}
              <span className="text-emerald-600 dark:text-emerald-400">{auditInfo.current_plan || 'Pro'}</span>
            </p>
          </div>
        </div>
      </Card>
    );
  }
  
  return (
    <Card className={cn("p-6", colors.cardBg)}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>
            Billing Audit Log
          </h3>
          <p className={cn("text-sm", colors.textMuted)}>
            Complete record of all billing events from Stripe and internal actions
          </p>
          {auditInfo.organization_name && isAdmin && (
            <p className={cn("text-sm mt-1", colors.textAccent)}>
              Organization: {auditInfo.organization_name}
            </p>
          )}
          {stripeCustomerId && (
            <p className={cn("text-xs font-mono mt-1", colors.textMuted)}>
              Stripe: {stripeCustomerId}
            </p>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          {/* Filter */}
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className={cn("w-[160px]", colors.inputBg)}>
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              <SelectItem value="invoice">Invoices</SelectItem>
              <SelectItem value="refund">Refunds</SelectItem>
              <SelectItem value="subscription_created">Subscription Started</SelectItem>
              <SelectItem value="subscription_cancelled">Cancelled</SelectItem>
              <SelectItem value="seat_added">Seats Added</SelectItem>
              <SelectItem value="seat_removed">Seats Removed</SelectItem>
              <SelectItem value="payment_failed">Payment Failed</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Refresh */}
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => fetchAuditLog(true)}
            disabled={refreshing}
          >
            <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} />
          </Button>
        </div>
      </div>
      
      {/* Summary */}
      {auditInfo.current_plan && (
        <div className={cn("flex flex-wrap gap-4 p-4 rounded-lg mb-6", colors.pageBg)}>
          <div>
            <span className={cn("text-xs uppercase", colors.textMuted)}>Current Plan</span>
            <p className={cn("font-medium", colors.textPrimary)}>{auditInfo.current_plan}</p>
          </div>
          <div>
            <span className={cn("text-xs uppercase", colors.textMuted)}>Status</span>
            <Badge className={cn(getStatusStyle(auditInfo.subscription_status).bg, getStatusStyle(auditInfo.subscription_status).text)}>
              {auditInfo.subscription_status?.replace(/_/g, ' ')}
            </Badge>
          </div>
          <div>
            <span className={cn("text-xs uppercase", colors.textMuted)}>Total Events</span>
            <p className={cn("font-medium", colors.textPrimary)}>{auditInfo.total_count}</p>
          </div>
        </div>
      )}

      {/* Refund History Summary */}
      {(() => {
        const refunds = events.filter(e => e.event_type === 'refund');
        if (refunds.length === 0) return null;
        
        const totalRefunded = refunds.reduce((sum, r) => sum + Math.abs(r.amount_cents || 0), 0);
        
        return (
          <div className={cn("p-4 rounded-lg mb-6 border border-orange-200 bg-orange-50/50")}>
            <div className="flex items-center gap-2 mb-3">
              <RotateCcw className="w-5 h-5 text-orange-500" />
              <h4 className="font-semibold text-orange-700">Refund History</h4>
              <Badge className="bg-orange-100 text-orange-700 ml-auto">
                {refunds.length} refund{refunds.length !== 1 ? 's' : ''}
              </Badge>
            </div>
            <div className="space-y-2">
              {refunds.slice(0, 5).map((refund) => (
                <div 
                  key={refund.event_id} 
                  className="flex items-center justify-between text-sm py-2 border-b border-orange-100 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-orange-600">
                      {formatCurrency(Math.abs(refund.amount_cents), refund.currency)}
                    </span>
                    <span className={colors.textMuted}>•</span>
                    <span className={colors.textMuted}>
                      {formatDate(refund.timestamp)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {refund.actor_email ? (
                      <>
                        <User className="w-3 h-3 text-orange-500" />
                        <span className="text-orange-600">{refund.actor_email}</span>
                      </>
                    ) : (
                      <span className={colors.textMuted}>Stripe</span>
                    )}
                  </div>
                </div>
              ))}
              {refunds.length > 5 && (
                <p className={cn("text-xs text-center pt-2", colors.textMuted)}>
                  + {refunds.length - 5} more refunds
                </p>
              )}
            </div>
            <div className="mt-3 pt-3 border-t border-orange-200 flex justify-between items-center">
              <span className="text-sm font-medium text-orange-700">Total Refunded</span>
              <span className="font-bold text-orange-600">
                {formatCurrency(totalRefunded, refunds[0]?.currency || 'USD')}
              </span>
            </div>
          </div>
        );
      })()}
      
      {/* Events List */}
      {events.length === 0 ? (
        <div className="text-center py-12">
          <Receipt className={cn("w-12 h-12 mx-auto mb-4 opacity-50", colors.textMuted)} />
          <p className={colors.textMuted}>No billing events found</p>
          <p className={cn("text-sm", colors.textMuted)}>Events will appear here once billing activity begins.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map((event) => {
            const eventConfig = getEventConfig(event.event_type);
            const statusStyle = getStatusStyle(event.status);
            const IconComponent = eventConfig.icon;
            const isExpanded = expandedEvent === event.event_id;
            
            return (
              <div 
                key={event.event_id}
                className={cn(
                  "border rounded-lg transition-all",
                  colors.cardBorder,
                  event.event_type === 'refund' && "border-orange-200 bg-orange-50/30",
                  isExpanded ? colors.cardBg : "hover:bg-white/5"
                )}
              >
                {/* Main Row */}
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer"
                  onClick={() => toggleEventExpand(event.event_id)}
                >
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "p-2 rounded-lg", 
                      event.event_type === 'refund' ? "bg-orange-100" : colors.pageBg
                    )}>
                      <IconComponent className={cn("w-5 h-5", eventConfig.color)} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={cn("font-medium", colors.textPrimary)}>
                          {eventConfig.label}
                        </span>
                        <Badge className={cn(statusStyle.bg, statusStyle.text, "text-xs")}>
                          {statusStyle.label}
                        </Badge>
                        {event.source === 'stripe' && (
                          <Badge variant="outline" className="text-xs">Stripe</Badge>
                        )}
                        {event.source === 'internal' && event.event_type === 'refund' && (
                          <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                            Admin Action
                          </Badge>
                        )}
                      </div>
                      <p className={cn("text-sm", colors.textMuted)}>
                        {event.description}
                      </p>
                      {/* Show admin info inline for refunds */}
                      {event.event_type === 'refund' && event.actor_email && (
                        <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                          <User className="w-3 h-3" />
                          Processed by {event.actor_email}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {event.amount_cents !== null && event.amount_cents !== undefined && (
                      <span className={cn(
                        "font-medium",
                        event.amount_cents < 0 ? "text-orange-500" : colors.textPrimary
                      )}>
                        {formatCurrency(event.amount_cents, event.currency)}
                      </span>
                    )}
                    {/* Refund Button for Admin */}
                    {canRefund(event) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          openRefundModal(event);
                        }}
                        title="Issue Refund"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                    )}
                    <div className="text-right">
                      <span className={cn("text-sm", colors.textMuted)}>
                        {formatDate(event.timestamp)}
                      </span>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className={cn("w-5 h-5", colors.textMuted)} />
                    ) : (
                      <ChevronDown className={cn("w-5 h-5", colors.textMuted)} />
                    )}
                  </div>
                </div>
                
                {/* Expanded Details */}
                {isExpanded && (
                  <div className={cn("px-4 pb-4 border-t", colors.cardBorder)}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                      {/* Actor Info */}
                      {(event.actor_email || event.actor_role) && (
                        <div>
                          <span className={cn("text-xs uppercase font-medium", colors.textMuted)}>
                            Performed By
                          </span>
                          <div className="flex items-center gap-2 mt-1">
                            <User className="w-4 h-4" />
                            <span className={colors.textPrimary}>
                              {event.actor_email || 'System'}
                            </span>
                            {event.actor_role && (
                              <Badge variant="outline" className="text-xs">
                                {event.actor_role}
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Event ID */}
                      <div>
                        <span className={cn("text-xs uppercase font-medium", colors.textMuted)}>
                          Event ID
                        </span>
                        <p className={cn("text-sm font-mono mt-1", colors.textPrimary)}>
                          {event.event_id}
                        </p>
                      </div>
                      
                      {/* Payment Method */}
                      {event.payment_method && (
                        <div>
                          <span className={cn("text-xs uppercase font-medium", colors.textMuted)}>
                            Payment Method
                          </span>
                          <div className="flex items-center gap-2 mt-1">
                            <CreditCard className="w-4 h-4" />
                            <span className={colors.textPrimary}>
                              {event.payment_method.brand?.toUpperCase()} •••• {event.payment_method.last4}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* Invoice Links */}
                      {(event.invoice_pdf || event.invoice_url) && (
                        <div>
                          <span className={cn("text-xs uppercase font-medium", colors.textMuted)}>
                            Documents
                          </span>
                          <div className="flex items-center gap-3 mt-1">
                            {event.invoice_pdf && (
                              <a 
                                href={event.invoice_pdf} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className={cn("flex items-center gap-1 text-sm hover:underline", colors.textAccent)}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Download className="w-4 h-4" /> PDF
                              </a>
                            )}
                            {event.invoice_url && (
                              <a 
                                href={event.invoice_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className={cn("flex items-center gap-1 text-sm hover:underline", colors.textAccent)}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink className="w-4 h-4" /> View Invoice
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Metadata */}
                      {event.metadata && Object.keys(event.metadata).length > 0 && (
                        <div className="md:col-span-2">
                          <span className={cn("text-xs uppercase font-medium", colors.textMuted)}>
                            Details
                          </span>
                          <div className={cn("mt-2 p-3 rounded-lg text-sm font-mono", colors.pageBg)}>
                            {Object.entries(event.metadata)
                              .filter(([key]) => !['lines', 'source'].includes(key))
                              .map(([key, value]) => (
                                <div key={key} className="flex justify-between py-1">
                                  <span className={colors.textMuted}>
                                    {key.replace(/_/g, ' ')}:
                                  </span>
                                  <span className={colors.textPrimary}>
                                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                  </span>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Invoice Line Items */}
                      {event.metadata?.lines && event.metadata.lines.length > 0 && (
                        <div className="md:col-span-2">
                          <span className={cn("text-xs uppercase font-medium", colors.textMuted)}>
                            Line Items
                          </span>
                          <div className={cn("mt-2 rounded-lg overflow-hidden border", colors.cardBorder)}>
                            <table className="w-full text-sm">
                              <thead className={colors.pageBg}>
                                <tr>
                                  <th className="text-left p-2">Description</th>
                                  <th className="text-right p-2">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {event.metadata.lines.map((line, idx) => (
                                  <tr key={idx} className={cn("border-t", colors.cardBorder)}>
                                    <td className="p-2">{line.description}</td>
                                    <td className="p-2 text-right">
                                      {formatCurrency(line.amount, event.currency)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Load More */}
      {auditInfo.has_more && (
        <div className="text-center mt-6">
          <Button variant="outline" onClick={() => toast.info('Load more coming soon')}>
            Load More Events
          </Button>
        </div>
      )}

      {/* Refund Modal */}
      <Dialog open={!!refundModal} onOpenChange={() => setRefundModal(null)}>
        <DialogContent className={cn("max-w-md", colors.cardBg)}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", colors.textPrimary)}>
              <RotateCcw className="w-5 h-5 text-orange-500" />
              Issue Refund
            </DialogTitle>
            <DialogDescription className={colors.textMuted}>
              Process a refund for this charge. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {refundModal && (
            <div className="space-y-4">
              {/* Original Transaction Info */}
              <div className={cn("p-3 rounded-lg", colors.pageBg)}>
                <div className="flex justify-between items-center mb-2">
                  <span className={cn("text-sm", colors.textMuted)}>Original Charge</span>
                  <span className={cn("font-medium", colors.textPrimary)}>
                    {formatCurrency(refundModal.amount_cents, refundModal.currency)}
                  </span>
                </div>
                <p className={cn("text-xs", colors.textMuted)}>
                  {formatDate(refundModal.timestamp)}
                </p>
                {refundModal.stripe_id && (
                  <p className={cn("text-xs font-mono mt-1", colors.textMuted)}>
                    {refundModal.stripe_id}
                  </p>
                )}
              </div>

              {/* Refund Amount */}
              <div>
                <Label className={colors.textPrimary}>Refund Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={refundModal.amount_cents / 100}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="mt-1"
                />
                <p className={cn("text-xs mt-1", colors.textMuted)}>
                  Maximum refund: {formatCurrency(refundModal.amount_cents, refundModal.currency)}
                </p>
              </div>

              {/* Reason */}
              <div>
                <Label className={colors.textPrimary}>Reason (optional)</Label>
                <Input
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="e.g., Customer request, duplicate charge"
                  className="mt-1"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundModal(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleRefund}
              disabled={refunding || !refundAmount || parseFloat(refundAmount) <= 0}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {refunding ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              Process Refund
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default BillingAuditLog;
